import React, { createContext, ReactElement, useEffect, useState } from 'react'

import InlinePrompt, { InlinePromptProps } from '../InlinePrompt/InlinePrompt'
import { ReportTabID } from '../../../ConfigureReport/Constants'

/**
 * Tab action prompt context type.
 */
type TabActionPromptType = {
    displayPrompt: (
        message: string,
        confirmText: string,
        cancelText: string,
        confirmCallback?: () => void,
        cancelCallback?: () => void
    ) => void
    closePrompt: () => void
}

/**
 * Tab action prompt provider props type.
 */
type TabActionPromptProviderProps = {
    children: React.ReactNode
    activeTabID: ReportTabID
}

/**
 * Provides access to prompt resource.
 * @param children Children to render.
 * @param activeTabID The ID of the active tab.
 */
export const TabActionPromptContextProvider = ({
    children,
    activeTabID
}: TabActionPromptProviderProps): ReactElement => {
    const [promptSettings, setPromptSettings] = useState<InlinePromptProps>()
    const [isShowing, setIsShowing] = useState(false)

    /**
     * A method that is called on the confirmation of the prompt.
     */
    const onConfirmPromptClose = (onConfirmFunc: (() => void) | undefined): void => {
        if (onConfirmFunc) {
            onConfirmFunc()
        }
        setIsShowing(false)
    }

    /**
     * A method that is called on the cancelling of the prompt.
     */
    const onCancelPromptClose = (onCancelFunc: (() => void) | undefined): void => {
        if (onCancelFunc) {
            onCancelFunc()
        }
        setIsShowing(false)
    }

    useEffect((): void => {
        // Cancel prompt when tab is switched.
        promptSettings?.onCancel && promptSettings.onCancel()
    }, [activeTabID])

    /**
     * Method that displays the requested prompt with input defined.
     *
     * @param message The prompt message that wants displaying.
     * @param confirmButtonText The text for the confirmation button.
     * @param cancelButtonText The text for the cancellation button.
     * @param onConfirm The callback function called on the confirming of the prompt.
     * @param onCancel The callback function called on the cancelling of the prompt.
     */
    const displayPrompt = (
        message: string,
        confirmButtonText: string,
        cancelButtonText: string,
        onConfirm?: () => void,
        onCancel?: () => void
    ): void => {
        setPromptSettings({
            message,
            onConfirm: () => {
                onConfirmPromptClose(onConfirm)
            },
            confirmButtonText,
            onCancel: () => {
                onCancelPromptClose(onCancel)
            },
            cancelButtonText
        })

        // Display prompt.
        setIsShowing(true)
    }

    return (
        <TabActionPromptContext.Provider
            value={{
                displayPrompt,
                closePrompt: (): void => {
                    setIsShowing(false)
                }
            }}
        >
            {isShowing ? (
                <div className='inline-prompt-wrapper tab-area'>
                    {!!promptSettings && <InlinePrompt {...promptSettings} />}
                </div>
            ) : (
                children
            )}
        </TabActionPromptContext.Provider>
    )
}

/**
 * Context for prompt resource.
 */
// eslint-disable-next-line
const TabActionPromptContext = createContext<TabActionPromptType>(undefined as any)
export default TabActionPromptContext
